import './SignatureQuestionRender.css'

import { Button } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import SignatureCanvas from 'react-signature-canvas'

import cn from '@/utils/tailwind/cn'

type Props = {
  answer?: {
    id?: any
    image: string | undefined
    text: string | undefined
    created_at?: string
    untrimmedImage?: string | undefined
  }
  disabled: boolean
  readonly?: boolean | undefined
  displayOnly?: boolean | undefined
  onChange: (value: Array<string | null | undefined>) => undefined
  onReset: () => undefined
  image: string | undefined
  untrimmedImage?: string | undefined
  label?: string
  forceClearButton?: boolean
  small?: boolean
  placeholder?: string
  forceSignatureLine?: boolean
}

const img = new Image()

function SignatureLine({
  placeholder,
  showPlaceholder,
}: {
  placeholder?: string
  showPlaceholder?: boolean
}) {
  if (!showPlaceholder) return null

  if (!placeholder) {
    return (
      <span className="cursor absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none text-sm text-gray-600">
        Draw your signature here
      </span>
    )
  }
  return (
    <div className="signature-placeholder-pad absolute left-1/2 top-1/2 inline -translate-x-1/2 -translate-y-1/2 select-none">
      CLICK TO SIGN
    </div>
  )
}

function Signature({
  answer,
  disabled,
  readonly,
  displayOnly,
  onChange,
  onReset,
  image,
  label,
  forceClearButton,
  small,
  placeholder,
  forceSignatureLine,
}: Props) {
  const sigCanvas = useRef<SignatureCanvas | null>(null)
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(!image)

  const onEnd = useCallback(() => {
    if (disabled || readonly) return

    if (sigCanvas.current?.isEmpty()) {
      onChange([null, null])
      return
    }

    const signature = sigCanvas.current?.toDataURL('image/png')

    onChange([signature, signature])
  }, [disabled, onChange, readonly])

  const onClear = useCallback(() => {
    if (disabled || readonly) return

    sigCanvas.current?.clear()
    onChange([null, null])
  }, [disabled, onChange, readonly])

  useEffect(() => {
    const imageResource = answer?.untrimmedImage || answer?.image

    if (sigCanvas?.current && imageResource) {
      sigCanvas.current?.fromDataURL(imageResource)
    }
  }, [answer])

  img.onload = useCallback(
    function (this: GlobalEventHandlers, ev: Event) {
      const target = ev.target as HTMLImageElement
      const imageWidth = target.width ?? 100
      const imageHeight = target.height ?? 100
      if (imageWidth > 15 || imageHeight > 15) return
      onClear()
    },
    [onClear]
  )

  if (disabled || readonly) {
    return (
      <div
        className={cn('relative rounded border border-gray-50', {
          'cursor-crosshair': !image && !placeholder,
          'cursor-pointer': !image && placeholder,
        })}
        style={{
          width: '100%',
          height: small ? 150 : 240,
          background: displayOnly ? 'white' : '#f4f5f7',
        }}
      >
        {image && (
          <img
            alt={label}
            className="h-full w-full object-scale-down"
            src={image}
          />
        )}
        {forceSignatureLine ? (
          <SignatureLine
            {...{ showPlaceholder: showPlaceholder && !image, placeholder }}
          />
        ) : null}
        {(forceClearButton || !readonly) && (
          <Button
            className="absolute right-1 top-1 h-8 px-2 text-sm !text-red-500 !outline-none hover:!bg-red-500/10"
            onClick={() => {
              onReset()
              setShowPlaceholder(true)
            }}
            type="link"
          >
            Clear
          </Button>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative w-full rounded border border-gray-100 bg-gray-50',
        {
          'cursor-crosshair': !image && !placeholder,
          'cursor-pointer': !image && placeholder,
        }
      )}
    >
      <SignatureCanvas
        canvasProps={{
          className: 'w-full',
          style: { height: 240 },
        }}
        onBegin={() => setShowPlaceholder(false)}
        onEnd={onEnd}
        penColor="black"
        ref={sigCanvas}
      />
      <SignatureLine
        {...{ showPlaceholder: showPlaceholder && !image, placeholder }}
      />

      {!(disabled || readonly) && (
        <Button
          className="absolute right-1 top-1 h-8 px-2 text-sm !text-red-500 !outline-none hover:!bg-red-500/10"
          onClick={() => {
            onClear()
            setShowPlaceholder(true)
          }}
          type="link"
        >
          Clear
        </Button>
      )}
    </div>
  )
}

export default Signature
